'use client';

import * as React from 'react';
import * as SwitchPrimitives from '@radix-ui/react-switch';

import { cn } from './utils';

/**
 * Toggle switch — visual parity to DS 1.5.0 `.acc-switch`
 * (`docs/design-system/1.5.0/templates/app/account.html` L86-89):
 *
 * - Root: a 42×24 pill (`border-box`, so the 1px `--border` stroke is inside
 *   the 42×24), `border-radius:12px`, off-fill `--bg-sunken`; on-state swaps
 *   both fill and border to `--clt-skyline`.
 * - Thumb: an 18px knob inset 2px from the pill edge (top:2px/left:2px via
 *   `items-center` + `translate-x-[2px]`), off-fill `--fg-2`; on-state slides
 *   it to `translate-x-[20px]` (DS `left:20px`) and turns it white
 *   (`--switch-knob-on`, theme-invariant).
 *
 * All colors are theme-aware CSS vars, so light/dark flip via
 * `[data-theme='dark']` without per-state `dark:` overrides.
 *
 * Blast radius: every `ToggleRow` (settings Notifications + Privacy, plus the
 * `SearchFiltersSheet`) and `BrowserPushToggle`. Sizing the shared default to
 * the DS is a deliberate fleet-wide alignment — `.acc-switch` is the intended
 * standard toggle, so it's net-neutral/improvement everywhere it renders (no
 * consumer wants a different toggle size). See FRE-1164.
 */
function Switch({ className, ...props }: React.ComponentProps<typeof SwitchPrimitives.Root>) {
  return (
    <SwitchPrimitives.Root
      data-slot="switch"
      className={cn(
        'peer relative inline-flex h-6 w-[42px] shrink-0 cursor-pointer items-center rounded-[12px] border border-ds-border bg-bg-sunken outline-none transition-colors focus-visible:ring-2 focus-visible:ring-clt-skyline-wash disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-clt-skyline data-[state=checked]:bg-clt-skyline',
        className,
      )}
      {...props}
    >
      <SwitchPrimitives.Thumb
        data-slot="switch-thumb"
        className={cn(
          'pointer-events-none block size-[18px] translate-x-[2px] rounded-full bg-fg-2 transition-transform duration-[160ms] data-[state=checked]:translate-x-5 data-[state=checked]:bg-switch-knob-on',
        )}
      />
    </SwitchPrimitives.Root>
  );
}

export { Switch };
